/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\LogProfileQuickListModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Log Profiles module.
----------------------------------------------------------------*/


import { FileText } from 'lucide-react';
import { QuickSearchModal } from '../../../components/ui/QuickSearchModal';
import { useI18n } from '../../../hooks/useI18n';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyProfile = { id?: string; name?: string; description?: string; [key: string]: any };

interface Props {
    isOpen: boolean;
    quickListSearch: string;
    filteredQuickList: AnyProfile[];
    isQuickListLoading: boolean;
    currentItemId?: string | null;
    onClose: () => void;
    onSearchChange: (v: string) => void;
    onSelect: (id: string) => void;
}

export function LogProfileQuickListModal({
    isOpen, quickListSearch, filteredQuickList, isQuickListLoading,
    currentItemId, onClose, onSearchChange, onSelect,
}: Props) {
    const { t } = useI18n();

    return (
        <QuickSearchModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('logProfiles.quickList', 'Log Profiles')}
            items={filteredQuickList.filter((p): p is typeof p & { id: string } => !!p.id)}
            isLoadingInitial={isQuickListLoading}
            isSearching={false}
            searchValue={quickListSearch}
            onSearchChange={onSearchChange}
            onItemSelect={(profile) => { onClose(); onSelect(profile.id as string); }}
            currentItemId={currentItemId ?? null}
            renderItem={(profile, isCurrent) => (
                <div className="flex items-center justify-between">
                    <div>
                        <p className="font-medium text-foreground">{profile.name}</p>
                        {profile.description && <p className="text-sm text-muted-foreground">{profile.description}</p>}
                    </div>
                    {isCurrent && (
                        <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 rounded">
                            {t('logProfiles.current', 'Current')}
                        </span>
                    )}
                </div>
            )}
            searchPlaceholder={t('logProfiles.quickListSearch', 'Search by name or description')}
            emptyMessage={t('logProfiles.quickListPrompt', 'Start typing to search...')}
            noResultsMessage={t('logProfiles.quickListNoResults', 'No profiles found')}
            headerIcon={<FileText className="h-5 w-5" />}
            minSearchChars={0}
        />
    );
}
